import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';

export type SkeletonLineSize =
  | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  | 'p' | 'body' | 'body-field' | 'body-s' | 'body-xs' | 'body-xxs'
  | 'table';

export type SkeletonCircleSize = 'sm' | 'md' | 'lg' | 'table';
export type SkeletonCircleShape = 'square' | 'rounded' | 'circle';

const shimmer = keyframes`
  100% {
    transform: translateX(100%);
  }
`;

const SkeletonBase = styled.span`
  display: inline-block;
  vertical-align: top;
  background: var(--ui-skeleton-base, rgba(0, 0, 0, 0.09));
  position: relative;
  overflow: hidden;

  &::before {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    background-image: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0,
      rgba(255, 255, 255, 0.2) 20px,
      rgba(255, 255, 255, 0.2) 60px,
      rgba(255, 255, 255, 0)
    );
    animation: ${shimmer} 1.5s infinite;
    content: '';
  }
`;

const lineSizeStyles: Record<SkeletonLineSize, ReturnType<typeof css>> = {
  h1: css`height: ${rem(36)}; line-height: ${rem(40)};`,
  h2: css`height: ${rem(32)}; line-height: ${rem(38)};`,
  h3: css`height: ${rem(26)}; line-height: ${rem(30)};`,
  h4: css`height: ${rem(22)}; line-height: ${rem(25)};`,
  h5: css`height: ${rem(20)}; line-height: ${rem(24)};`,
  h6: css`height: ${rem(16)}; line-height: ${rem(18)};`,
  p: css`height: ${rem(36)}; line-height: ${rem(40)};`,
  body: css`height: ${rem(16)}; line-height: ${rem(22)};`,
  'body-field': css`height: ${rem(16)}; line-height: ${rem(16)};`,
  'body-s': css`height: ${rem(14)}; line-height: ${rem(18)};`,
  'body-xs': css`height: ${rem(12)}; line-height: ${rem(14)};`,
  'body-xxs': css`height: ${rem(10)}; line-height: ${rem(12)};`,
  table: css`height: ${rem(28)}; line-height: ${rem(28)};`,
};

const StyledLine = styled(SkeletonBase).withConfig({
  shouldForwardProp: (prop) => !['$size', '$width'].includes(String(prop)),
})<{ $size: SkeletonLineSize; $width: string }>`
  border-radius: var(--ui-radius-sm, 5px);
  width: ${({ $width }) => $width};
  ${({ $size }) => lineSizeStyles[$size]};
`;

export interface SkeletonLineProps extends React.HTMLAttributes<HTMLSpanElement> {
  size?: SkeletonLineSize;
  width?: string;
  semanticId?: string;
}

export const SkeletonLine = React.forwardRef<HTMLSpanElement, SkeletonLineProps>(
  function SkeletonLine({ size = 'table', width = '100%', semanticId, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return <StyledLine ref={ref} $size={size} $width={width} {...semanticProps} {...rest} />;
  },
);

SkeletonLine.displayName = 'SkeletonLine';

const circleSizeStyles: Record<SkeletonCircleSize, ReturnType<typeof css>> = {
  sm: css`height: ${rem(16)}; width: ${rem(16)};`,
  md: css`height: ${rem(30)}; width: ${rem(30)};`,
  lg: css`height: ${rem(40)}; width: ${rem(40)};`,
  table: css`height: ${rem(28)}; width: ${rem(28)};`,
};

const circleShapeStyles: Record<SkeletonCircleShape, ReturnType<typeof css>> = {
  circle: css`border-radius: 50%;`,
  rounded: css`border-radius: var(--ui-radius-sm, 10%);`,
  square: css`border-radius: var(--ui-radius-none, 0px);`,
};

const StyledCircle = styled(SkeletonBase).withConfig({
  shouldForwardProp: (prop) => !['$size', '$shape'].includes(String(prop)),
})<{ $size: SkeletonCircleSize; $shape: SkeletonCircleShape }>`
  border-radius: var(--ui-radius-sm, 5px);
  ${({ $size }) => circleSizeStyles[$size]};
  ${({ $shape }) => circleShapeStyles[$shape]};
`;

export interface SkeletonCircleProps extends React.HTMLAttributes<HTMLSpanElement> {
  size?: SkeletonCircleSize;
  shape?: SkeletonCircleShape;
  semanticId?: string;
}

export const SkeletonCircle = React.forwardRef<HTMLSpanElement, SkeletonCircleProps>(
  function SkeletonCircle({ size = 'table', shape = 'rounded', semanticId, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return <StyledCircle ref={ref} $size={size} $shape={shape} {...semanticProps} {...rest} />;
  },
);

SkeletonCircle.displayName = 'SkeletonCircle';
